import { CommentDraft, DraftItem, TopicDraft } from '../types';

type DraftState = {
  topics?: TopicDraft[];
  comments?: Record<string, CommentDraft>;
};

const KEY_PREFIX = 'drafts:v1:';

const nowIso = () => new Date().toISOString();

const readState = (userId: string): DraftState => {
  if (!userId) return {};
  try {
    const raw = localStorage.getItem(KEY_PREFIX + userId);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const topics = Array.isArray(parsed.topics)
      ? parsed.topics
      : parsed.topic
        ? [parsed.topic]
        : [];
    return {
      topics,
      comments: parsed.comments || {}
    };
  } catch {
    return {};
  }
};

const writeState = (userId: string, state: DraftState) => {
  try {
    localStorage.setItem(KEY_PREFIX + userId, JSON.stringify(state));
    try { window.dispatchEvent(new Event('drafts:changed')); } catch { /* ignore */ }
  } catch {
    // ignore persistence errors (storage full / disabled)
  }
};

export const getDraftState = (userId: string): DraftState => readState(userId);

export const getAllDrafts = (userId: string): DraftItem[] => {
  const state = readState(userId);
  const items: DraftItem[] = [];
  (state.topics || []).forEach((t) => items.push(t));
  Object.values(state.comments || {}).forEach((c) => items.push(c));
  return items.sort((a, b) => {
    const aTime = new Date(a.updatedAt || 0).getTime();
    const bTime = new Date(b.updatedAt || 0).getTime();
    return bTime - aTime;
  });
};

export const saveTopicDraft = (
  userId: string,
  data: Omit<TopicDraft, 'id' | 'type' | 'userId' | 'updatedAt'> & { id?: string }
): TopicDraft => {
  const state = readState(userId);
  const nextId = data.id || `topic-${userId}-${Date.now()}`;
  const draft: TopicDraft = {
    id: nextId,
    type: 'topic',
    userId,
    title: data.title || '',
    content: data.content || '',
    basemap: data.basemap,
    visibility: data.visibility,
    audienceUserIds: data.audienceUserIds,
    isNsfw: Boolean(data.isNsfw),
    isEditorial: Boolean(data.isEditorial),
    location: data.location,
    drawing: data.drawing,
    updatedAt: nowIso()
  };
  const topics = state.topics || [];
  const existingIndex = topics.findIndex((entry) => entry.id === nextId);
  const nextTopics = existingIndex >= 0
    ? topics.map((entry) => entry.id === nextId ? draft : entry)
    : [draft, ...topics];
  const next: DraftState = { ...state, topics: nextTopics };
  writeState(userId, next);
  return draft;
};

export const clearTopicDraft = (userId: string, draftId?: string) => {
  const state = readState(userId);
  if (!state.topics || state.topics.length === 0) return;
  if (!draftId) {
    const next: DraftState = { ...state, topics: [] };
    writeState(userId, next);
    return;
  }
  const nextTopics = state.topics.filter((draft) => draft.id !== draftId);
  const next: DraftState = { ...state, topics: nextTopics };
  writeState(userId, next);
};

export const saveCommentDraft = (userId: string, data: Omit<CommentDraft, 'id' | 'type' | 'userId' | 'updatedAt'>): CommentDraft => {
  const state = readState(userId);
  const draft: CommentDraft = {
    id: `comment-${data.topicId}`,
    type: 'comment',
    userId,
    topicId: data.topicId,
    topicTitle: data.topicTitle,
    content: data.content || '',
    location: data.location,
    drawing: data.drawing,
    updatedAt: nowIso()
  };
  const next: DraftState = {
    ...state,
    comments: {
      ...(state.comments || {}),
      [data.topicId]: draft
    }
  };
  writeState(userId, next);
  return draft;
};

export const clearCommentDraft = (userId: string, topicId: string) => {
  const state = readState(userId);
  if (!state.comments) return;
  const next = { ...state.comments };
  delete next[topicId];
  writeState(userId, { ...state, comments: next });
};
